import React, { useState, useEffect } from 'react';
import { Box, Paper } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Chart from './Chart';
import ZoomableDiagram from './ZoomableDiagram';
import { SENSOR_LABELS as PYLON2_LEFT_LABELS } from '../config/sensorLabels/pylon2LeftLeg';
import { SENSOR_LABELS as PYLON2_RIGHT_LABELS } from '../config/sensorLabels/pylon2RightLeg';

/** Assets live in public/pylon 2, 3/ — encode for safe URLs */
const pylon23Asset = (fileName) =>
  `/${encodeURIComponent('pylon 2, 3')}/${encodeURIComponent(fileName)}`;

/** Zoom inside fixed diagram panel (box size unchanged; edges may clip) */
const PYLON2_INITIAL_ZOOM = 1.35;

const Pylon2Board = ({ legType = 'left' }) => {
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState('Navigation');
  const [structureImage, setStructureImage] = useState(() => pylon23Asset('nav.png'));

  // Map tab values to image filenames based on leg type
  // Using actual filenames from public folder
  const getTabImageMap = () => {
    const baseMap = {
      'Navigation': 'nav.png',
      'Elev 768.9': 'elev 768.9.png',
      'Elev 601.9': 'pylon 2 elev 601.png', // Shared image for both legs
      'Elev 529.5': 'pylon 2 elev 529.png', // Shared image
      'Elev 469.6': 'pylon 2 elev 469.png', // Shared image
      'A': 'ELE 760.png',
      'B': 'ELE 748.png',
      'C': 'ELE 730.png',
      'D': 'ELE 689.png'
    };
    return baseMap;
  };

  // Update image when tab changes
  useEffect(() => {
    const tabImageMap = getTabImageMap();
    const imageFileName = tabImageMap[selectedTab];
    if (imageFileName) {
      const img = new Image();
      
      // For right leg, try right leg image first, then fallback to left leg image
      const tryFallback = () => {
        if (legType === 'right' && selectedTab !== 'Navigation' && selectedTab !== 'Elev 601.9' && selectedTab !== 'Elev 768.9' && selectedTab !== 'A' && selectedTab !== 'B' && selectedTab !== 'C' && selectedTab !== 'D') {
          const leftLegMap = {
            'Elev 529.5': 'pylon 2 elev 529.png',
            'Elev 469.6': 'pylon 2 elev 469.png'
          };
          const fallbackImage = leftLegMap[selectedTab];
          if (fallbackImage) {
            const fallbackImg = new Image();
            fallbackImg.onerror = () => {
              setStructureImage('/new_bridge_rendering.png');
            };
            fallbackImg.onload = () => {
              setStructureImage(pylon23Asset(fallbackImage));
            };
            fallbackImg.src = pylon23Asset(fallbackImage);
            return;
          }
        }
        setStructureImage('/new_bridge_rendering.png');
      };
      
      img.onerror = tryFallback;
      img.onload = () => {
        setStructureImage(pylon23Asset(imageFileName));
      };
      img.src = pylon23Asset(imageFileName);
    } else {
      setStructureImage('/new_bridge_rendering.png');
    }
  }, [selectedTab, legType]);

  useEffect(() => {
    const allowedTabs = ['Navigation', 'Elev 768.9', 'A', 'B', 'C', 'D', 'Elev 601.9', 'Elev 529.5', 'Elev 469.6'];
    const tabFromUrl = new URLSearchParams(location.search).get('tab') || 'Navigation';
    setSelectedTab(allowedTabs.includes(tabFromUrl) ? tabFromUrl : 'Navigation');
  }, [location.search]);

  const legLabel = legType === 'left' ? 'Left Leg' : 'Right Leg';
  const sensorLabels = legType === 'left' ? PYLON2_LEFT_LABELS : PYLON2_RIGHT_LABELS;
  const panelHeight = 'calc(100vh - 235px)';

  return (
    <Box sx={{ 
      p: 0, 
      pt: 0, 
      width: '100%',
      maxWidth: 'none',
      mx: 0,
      minWidth: 0,
      bgcolor: 'white'
    }}>
      <Box
        sx={{
          display: 'flex',
          gap: 1.25,
          alignItems: 'stretch',
          mt: 0,
          mb: 0,
          px: 0,
          flexWrap: 'nowrap',
          height: panelHeight
        }}
      >
        <ZoomableDiagram
          src={structureImage}
          alt={`Pylon 2 ${legLabel} - ${selectedTab}`}
          initialZoom={PYLON2_INITIAL_ZOOM}
          onImageError={(e) => {
            e.target.src = '/new_bridge_rendering.png';
          }}
        />

        <Paper elevation={2} sx={{ p: 1.5, flex: 1, minWidth: 0, width: '100%', height: '100%', overflow: 'auto' }}>
          <Chart compact pier={1} hideTabsAndImage sensorLabels={sensorLabels} />
        </Paper>
      </Box>
    </Box>
  );
};

export default Pylon2Board;
